import { useEffect } from 'react'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { api } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Logo } from '../../components/brand/Logo'
import { Icon, type IconName } from '../../components/ui/icons'
import { formatRut } from '../../utils/format'

export interface PortalMe {
  rut: string
  firstName: string
  lastName: string
  email?: string | null
  insurance: string
}

interface PortalSummary {
  upcomingAppointments: number
  documents: number
  followUps: number
  payments: number
  /** Recetas, órdenes y certificados: una sola pestaña, un solo contador. */
  clinicalRecords: number
}

const NAV: Array<{ to: string; label: string; icon: IconName; badge: keyof PortalSummary }> = [
  { to: '/paciente', label: 'Mis citas', icon: 'agenda', badge: 'upcomingAppointments' },
  { to: '/paciente/examenes', label: 'Mis exámenes', icon: 'lab', badge: 'documents' },
  { to: '/paciente/recetas', label: 'Recetas y certificados', icon: 'prescription', badge: 'clinicalRecords' },
  { to: '/paciente/controles', label: 'Próximos controles', icon: 'scheduleCheck', badge: 'followUps' },
  { to: '/paciente/pagos', label: 'Pagos y boletas', icon: 'cashier', badge: 'payments' },
]

/**
 * Portal del paciente: header propio, navegación por secciones y el aviso de
 * qué se ve y qué no. Marcado como no indexable también desde el documento —
 * la API y nginx ya mandan `X-Robots-Tag`, pero el `<meta>` cubre el caso de
 * que alguien sirva la SPA sin ese proxy delante.
 */
export function PatientPortalLayout() {
  const { user, refreshToken, logout } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow, noarchive'
    document.head.appendChild(meta)
    return () => meta.remove()
  }, [])

  const me = useQuery({ queryKey: ['patient-me'], queryFn: async () => (await api.get<PortalMe>('/patient-portal/me')).data })
  const summary = useQuery({
    queryKey: ['patient-summary'],
    queryFn: async () => (await api.get<PortalSummary>('/patient-portal/summary')).data,
  })

  async function handleLogout() {
    try {
      if (refreshToken) await api.post('/auth/logout', { refreshToken })
    } finally {
      logout()
      // Al salir, el paciente vuelve al sitio público, no al formulario de
      // ingreso: quedar frente a un login que acaba de cerrar se lee como si
      // la sesión no hubiese terminado (pedido de Chejo, minuta 15-09).
      navigate('/')
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="bg-brand-900 text-white">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          {/* El logo lleva al sitio público, como en cualquier sitio: las
              secciones del portal ya están en la navegación de abajo. */}
          <Link to="/" title="Ir al sitio de Cima Sport" className="flex items-center gap-2 font-display text-lg">
            <Logo variant="white" className="h-8" />
            <span className="font-normal text-brand-300">· Mi portal</span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-brand-100 sm:inline">
              {user?.firstName} {user?.lastName}
            </span>
            <button onClick={handleLogout} className="rounded bg-brand-700 px-3 py-1.5 text-xs text-brand-100 hover:bg-brand-600">
              Cerrar sesión
            </button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-4xl gap-1 overflow-x-auto px-4 pb-2">
          {NAV.map((item) => {
            const count = summary.data?.[item.badge] ?? 0
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/paciente'}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm transition-colors',
                    isActive ? 'bg-brand-600 font-semibold text-white' : 'text-brand-100 hover:bg-brand-700',
                  )
                }
              >
                <Icon name={item.icon} className="h-4 w-4" />
                {item.label}
                {count > 0 && <span className="rounded-full bg-white/20 px-1.5 text-xs">{count}</span>}
              </NavLink>
            )
          })}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-4xl flex-1 space-y-4 p-4 md:p-6">
        {me.data && (
          <p className="text-sm text-gray-500">
            {me.data.firstName} {me.data.lastName} · {formatRut(me.data.rut)} · {me.data.insurance}
          </p>
        )}
        <Outlet context={me.data ?? null} />
        <p className="text-xs text-gray-400">
          Tu ficha clínica (anamnesis, evolución y diagnósticos) no se publica en el portal; puedes solicitarla en la
          clínica (Ley 20.584). Aquí ves tus citas, tus resultados de exámenes, tus controles y tus pagos.
        </p>
      </main>
    </div>
  )
}
